import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import {
  HelpCircle as HelpCircleIcon,
  PlusCircle as PlusCircleIcon,
} from 'react-feather'
import { Button, Card, Container, Tooltip } from '@material-ui/core'
import { isEmpty } from 'lodash'

import {
  Page,
  ContentHeader,
  Permitted,
  FilterButton,
  ButtonAction,
  DriveTour,
} from 'components'
import { ImportUsersDialog, UsersFilters, UsersTable } from './components'

import BulkSelectionProvider from 'providers/BulkSelectionProvider'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'
import usePagination from 'hooks/usePagination'
import usePermissions from 'hooks/usePermissions'

import * as service from 'service'
import { routes } from 'Routes'
import useStyles from './styles'
import constants from 'constants/index'
import helpers from 'helpers'

const UsersMain = () => {
  const [openTour, setOpenTour] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [profiles, setProfiles] = useState([])

  const filter = useFilter()
  const history = useHistory()
  const classes = useStyles()
  const permissions = usePermissions()

  const hasCreatePermission = permissions.permitted(
    constants.permissions.USERS.CREATE,
  )

  const hasEditPermission = permissions.permitted(
    constants.permissions.USERS.EDIT,
  )

  const hasDeletePermission = permissions.permitted(
    constants.permissions.USERS.SOFT_DELETE,
  )

  const pagination = usePagination(parseInt(filter.filters?.perPage) || 10)
  const { perPage, page } = pagination

  const { response, isLoading, refresh } = useFetch(
    service.dponet.usersCompanies.list,
    {
      listByCompany: true,
      listInactives: true,
      userListing: true,
      ...{ ...filter.filters },
      perPage,
      page,
    },
    [page, perPage, filter.filters],
  )

  const userCompanies = response?.data?.userCompanies

  const handleNew = () => {
    history.push(routes.user.new)
  }

  const handleOpenDialog = () => {
    helpers.mixpanel.track('Usuários', 'Importar Usuários', {
      action: 'importar_usuários-Usuários',
    })

    setOpenDialog(!openDialog)
  }

  const handleOpenTour = () => {
    const where = 'Usuários'
    const title = 'Tour'

    const params = {
      action: 'tour-usuários-empresas',
    }

    helpers.mixpanel.track(where, title, params)
    setOpenTour(true)
  }

  const filtersMatrixSteps = (elementId, permission) => {
    const requiredPermission =
      constants.user.USERS_DRIVER_STEPS_WITH_PERMISSIONS[elementId]

    if (
      (isEmpty(userCompanies) ||
        (!hasEditPermission && !hasDeletePermission)) &&
      elementId === '#' + constants.user.USER_MAIN_DRIVER_STEP_3
    )
      return false

    return requiredPermission ? permission(requiredPermission) : true
  }

  const filteredSteps = constants.user.USERS_DRIVER_STEPS_OBJECT.filter(
    (step) => {
      return filtersMatrixSteps(step.element, permissions.permitted)
    },
  )

  const disableTour =
    !hasCreatePermission &&
    ((!hasEditPermission && !hasDeletePermission) || isEmpty(userCompanies))

  useEffect(() => {
    const filteredPerPage = parseInt(filter.filters?.perPage)
    const isDefaultPerPage = perPage === 10

    if (isDefaultPerPage && !filteredPerPage) return
    if (!isDefaultPerPage && filteredPerPage === perPage) return

    filter.setFilters(({ perPage: previousPerPage, ...currentFilters }) => ({
      ...currentFilters,
      page,
      ...(isDefaultPerPage ? {} : { perPage }),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filter.filters?.perPage])

  useEffect(() => {
    const getProfiles = async () => {
      const response = await service.dponet.profiles.get({
        status: true,
        profileType: constants.profile.PROFILE_CLIENT_TYPE,
        perPage: 1000,
      })

      setProfiles(response?.data?.profiles)
    }

    getProfiles()
  }, [])

  return (
    <Page title="Todos os usuários">
      <Container maxWidth={false} className={classes.container}>
        <ContentHeader title="Usuários" smFull>
          <Tooltip
            title={
              disableTour
                ? "Para iniciar este tour guiado, é necessário que você possua permissões para 'criar', 'editar' ou 'excluir' como algum usuário definido."
                : 'Iniciar o tour guiado'
            }
          >
            <Button
              id={constants.user.USER_MAIN_DRIVER_STEP_0}
              variant="contained"
              color="primary"
              startIcon={<HelpCircleIcon size={20} />}
              onClick={handleOpenTour}
              disabled={disableTour}
              fullWidth
            >
              Tutorial
            </Button>
          </Tooltip>
          <Permitted tag={constants.permissions.USERS.CREATE}>
            <ButtonAction
              id={constants.user.USER_MAIN_DRIVER_STEP_1}
              variant="contained"
              color="primary"
              action={handleOpenDialog}
              name="IMPORTAR USUÁRIOS"
              fullWidth
            />
            <ButtonAction
              id={constants.user.USER_MAIN_DRIVER_STEP_2}
              variant="outlined"
              color="primary"
              action={handleNew}
              name="NOVO USUÁRIO"
              startIcon={<PlusCircleIcon width="18" />}
              fullWidth
            />
          </Permitted>
          <FilterButton setDrawerOpen={filter.setDrawerOpen} fullWidth />
        </ContentHeader>
        <BulkSelectionProvider items={userCompanies}>
          <Card>
            <UsersTable
              userCompanies={userCompanies}
              isLoading={isLoading}
              profiles={profiles}
              refresh={refresh}
              count={response?.data?.meta?.totalCount}
              pagination={pagination}
            />
          </Card>
        </BulkSelectionProvider>
        <UsersFilters filter={filter} profiles={profiles} />
      </Container>

      <DriveTour
        stepsMatrix={filteredSteps}
        customStyle={classes.customPopover}
        open={openTour}
        setOpen={setOpenTour}
      />

      <ImportUsersDialog
        profiles={profiles}
        open={openDialog}
        setOpen={setOpenDialog}
        refresh={refresh}
      />
    </Page>
  )
}

export default UsersMain
